import { useApolloClient } from '@apollo/client'
import { Theme } from '@eversports/react-components/design-tokens/theme'
import Button from '@eversports/react-components/primitives/Button'
import CircularProgress from '@eversports/react-components/primitives/CircularProgress'
import Flex from '@eversports/react-components/primitives/Flex'
import Text from '@eversports/react-components/primitives/Text'
import useAmplitude from '@eversports/amplitude-react/useAmplitude'
import { makeStyles } from '@mui/styles'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import Routes from '../../data/routes'
import useCheckoutContext from '../../data/useCheckoutContext'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { Localized, useIntl } from '../../localization/react'
import handlePaymentError from '../Payment/handle-payment-error'

import { BlockingError } from '../../lib/custom-errors'
import getUserTrackingPreferences from '../../data/get-user-tracking-preferences'
import {
  SummaryButtonCreateOrderFromCartDocument,
  SummaryButtonCreateOrderFromCartMutation,
  SummaryButtonCreateOrderFromCartMutationVariables,
  useSummaryButtonGetMarketplaceFeeLazyQuery,
  useSummaryButtonTrackCheckoutCompleteMutation,
} from '../../graphql'
import checkIfAdvertisingTrackingAllowed from '../../lib/check-if-advertising-tracking-allowed'
import useGooglePurcahseTracking from '../Payment/hooks/useGooglePurcahseTracking'

const useStyles = makeStyles((theme: Theme) => ({
  summaryButton: {
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.fontSizing.small,
    },
  },
}))

interface Props {
  isSettleWithAConfirmWithOption: boolean
  fullWidth?: boolean
}

type SummaryButtonError = { type: 'blockingError' | 'unexpectedError'; message: string }

const SummaryButton: React.FC<React.PropsWithChildren<Props>> = ({
  isSettleWithAConfirmWithOption,
  fullWidth = false,
}) => {
  const intl = useIntl()
  const history = useHistory()
  const location = useLocation()
  const apolloClient = useApolloClient()
  const classes = useStyles()
  const { sessionId, deviceId } = useAmplitude()
  const ref = useRef<HTMLButtonElement>(null)

  const [isOrderBeingCreated, setIsOrderBeingCreated] = useState(false)
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null)
  const [summaryButtonError, setSummaryButtonError] = useState<SummaryButtonError | null>(null)

  const { cartId, orderId } = getPhoenixUrlParams(location.search)
  const {
    isCurrentStepValid,
    getNextStep,
    setIsPaymentProcessing,
    isPaymentProcessing,
    processPayment,
    clearErrors,
    setError,
    getError,
  } = useCheckoutContext()

  const [trackCheckoutComplete] = useSummaryButtonTrackCheckoutCompleteMutation()

  const [getMarketplaceFee, { data: marketplaceFeeData }] = useSummaryButtonGetMarketplaceFeeLazyQuery()

  const isAdvertisingTrackingAllowed = checkIfAdvertisingTrackingAllowed()

  const isPaymentProviderInitializedOnPaymentPage =
    location.pathname === Routes.PAYMENT ? Boolean(processPayment) : true
  const canContinue =
    isCurrentStepValid(location.pathname as Routes) && !isPaymentProcessing && isPaymentProviderInitializedOnPaymentPage

  const { handleGooglePurchaseTracking } = useGooglePurcahseTracking()

  useEffect(() => {
    if (!marketplaceFeeData) return

    if (marketplaceFeeData.getMarketplaceFeeForOrder) {
      window.dataLayer.push({
        event: 'phoenix-mpf-tracking',
        mpfv: marketplaceFeeData.getMarketplaceFeeForOrder.value / 100,
        mpfc: marketplaceFeeData.getMarketplaceFeeForOrder.currency,
      })
    }

    // If we are on the payment page, we process the order normally
    if (orderId) {
      setIsPaymentProcessing(false)
      history.push({ pathname: getNextStep(), search: location.search })
    } else {
      // If we just programmatically created the order because there was no payment
      history.push({ pathname: Routes.CONFIRMATION, search: `?orderId=${createdOrderId}` })
    }
  }, [marketplaceFeeData])

  useEffect(() => {
    if (!summaryButtonError) return

    if (summaryButtonError.type === 'blockingError') {
      throw new BlockingError('An error happened when going to the next checkout step', summaryButtonError.message)
    }

    throw new Error(`There was an unexpected error creating the order from the cart: ${summaryButtonError.message}`)
  }, [summaryButtonError])

  const handleButtonClick = async () => {
    if (!canContinue) return

    if (location.pathname !== Routes.PAYMENT) {
      const nextStep = getNextStep()

      // create an order from cart if
      // - there is a payment that needs to be processed
      // - the product price is zero
      if (nextStep === Routes.PAYMENT || nextStep === Routes.CONFIRMATION) {
        setIsOrderBeingCreated(true)

        const { data, errors } = (await apolloClient.mutate<
          SummaryButtonCreateOrderFromCartMutation,
          SummaryButtonCreateOrderFromCartMutationVariables
        >({
          mutation: SummaryButtonCreateOrderFromCartDocument,
          variables: { cartId: cartId as string },
        })) as { data?: SummaryButtonCreateOrderFromCartMutation; errors?: Array<Error> }

        if (data && data.createOrderFromCart.__typename === 'Order') {
          const hasSomethingToPay =
            data.createOrderFromCart.priceTable &&
            data.createOrderFromCart.priceTable.total &&
            data.createOrderFromCart.priceTable.total.value > 0

          if (!hasSomethingToPay) {
            const hasGlParam = window.location.href.includes('_gl')
            if (hasGlParam && isAdvertisingTrackingAllowed) {
              await handleGooglePurchaseTracking({ orderId: data.createOrderFromCart.id as string })
            }

            const { isTrackingAllowed } = getUserTrackingPreferences()

            if (!isTrackingAllowed) {
              history.push({ pathname: Routes.CONFIRMATION, search: `?orderId=${data.createOrderFromCart.id}` })
            }

            if (deviceId && sessionId) {
              await trackCheckoutComplete({
                variables: {
                  orderId: data.createOrderFromCart.id,
                  deviceId,
                  sessionId: Number(sessionId),
                },
              })
            }

            setCreatedOrderId(data.createOrderFromCart.id)
            getMarketplaceFee({ variables: { orderId: data.createOrderFromCart.id } })
          } else {
            setIsOrderBeingCreated(false)
            history.push({ pathname: nextStep, search: `?orderId=${data.createOrderFromCart.id}` })
          }
        }

        // All errors in this process should block the UI due to the fact that the user is in the middle of the checkout
        // process and if something happens here it means they shouldn't be able to continue anymore
        if (errors) {
          setSummaryButtonError({ type: 'unexpectedError', message: errors[0].message })
        }

        if (data && data.createOrderFromCart.__typename === 'ExpectedErrors' && !getError('createOrderFromCart')) {
          const message = data.createOrderFromCart.errors && data.createOrderFromCart.errors[0].message
          setSummaryButtonError({ type: 'blockingError', message })
        }

        return
      }

      history.push({ pathname: nextStep, search: location.search })
      return
    }

    if (!processPayment) {
      throw new Error('processPayment needs to be set via context.setProcessPayment')
    }

    clearErrors()
    setIsPaymentProcessing(true)

    // reset the view to the top of the window in order for the loading spinner
    // to be shown
    window.scrollTo(0, 0)

    const { wasSuccessful, error } = await processPayment()

    if (wasSuccessful) {
      const hasGlParam = window.location.href.includes('_gl')
      if (hasGlParam && isAdvertisingTrackingAllowed) {
        await handleGooglePurchaseTracking({ orderId: orderId as string })
      }

      const { isTrackingAllowed } = getUserTrackingPreferences()

      if (!isTrackingAllowed) {
        history.push({ pathname: getNextStep(), search: location.search })
        return
      }

      if (deviceId && sessionId && orderId) {
        await trackCheckoutComplete({
          variables: { orderId, deviceId, sessionId: Number(sessionId) },
        })
      }

      if (orderId) {
        getMarketplaceFee({ variables: { orderId } })
      }
    } else if (error) {
      setIsPaymentProcessing(false)
      handlePaymentError(error, setError, intl)
    }
  }

  const summaryButtonText = () => {
    if (location.pathname !== Routes.PAYMENT && getNextStep() === Routes.CONFIRMATION) {
      return <Localized id="summary-button-confirm" />
    }

    if (location.pathname === Routes.PAYMENT) {
      return <Localized id="summary-button-pay" />
    }

    if (!isSettleWithAConfirmWithOption) {
      return <Localized id="summary-button-continue" />
    }

    return <Localized id="summary-button-book-now" />
  }

  return isOrderBeingCreated ? (
    <Flex flexDirection="column" justifyContent="center" alignItems="center" verticalSpacing="default">
      <CircularProgress />
      <Text data-testid="preparing-payment-page">
        <Localized id="preparing-your-order" />
      </Text>
    </Flex>
  ) : (
    <Button
      data-testid="summary-button-continue"
      variant="primary"
      fullWidth={fullWidth}
      disabled={!canContinue}
      onClick={handleButtonClick}
      ref={ref}
    >
      <Text fontWeight="bold" color="white" className={classes.summaryButton}>
        {summaryButtonText()}
      </Text>
    </Button>
  )
}

export default SummaryButton
